import * as React from 'react';
import { DragElementWrapper, DragSourceOptions, DropTarget, DragSource, DropTargetMonitor } from 'react-dnd';
import { Input, Button, Collapse, Tooltip, Popover, Modal, Select } from 'antd';
import {
    CommentOutlined,
    DeleteOutlined,
    DragOutlined,
    FlagOutlined,
    ToolOutlined,
    ThunderboltOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import TextArea from 'antd/lib/input/TextArea';
import { PromptTemplateBlock, PromptTemplateStructureEnum, PromptTemplateBlockTypeEnum } from '../../types';
import './PromptTemplate.less';

const { Panel } = Collapse;

/**
 * Returns the appropriate icon component for a given block type
 * Used to visually indicate the block type in orchestrated templates
 */
const getBlockTypeIcon = (blockType: PromptTemplateBlockTypeEnum) => {
    switch (blockType) {
        case PromptTemplateBlockTypeEnum.Preprocessing:
            return <ToolOutlined />;
        case PromptTemplateBlockTypeEnum.Processing:
            return <ThunderboltOutlined />;
        case PromptTemplateBlockTypeEnum.Postprocessing:
            return <CheckCircleOutlined />;
        case PromptTemplateBlockTypeEnum.Plain:
        default:
            return null;
    }
};

/**
 * Returns a human-readable label for a given block type
 * Used as tooltip text for the block type icon
 */
const getBlockTypeLabel = (blockType: PromptTemplateBlockTypeEnum) => {
    switch (blockType) {
        case PromptTemplateBlockTypeEnum.Preprocessing:
            return 'Preprocessing';
        case PromptTemplateBlockTypeEnum.Processing:
            return 'Processing';
        case PromptTemplateBlockTypeEnum.Postprocessing:
            return 'Postprocessing';
        case PromptTemplateBlockTypeEnum.Plain:
        default:
            return 'Plain';
    }
};

type BeginDragProps = DraggableBlockProps;

type BlockTargetProps = {
    moveBlock: (dragIndex: number, hoverIndex: number) => void;
    index: number;
};

const blockSource = {
    beginDrag(props: BeginDragProps) {
        props.onDragStart(props.index);
        return {
            index: props.index,
            block: props.block
        };
    }
};

const blockTarget = {
    drop(props: BlockTargetProps, monitor: DropTargetMonitor) {
        const dragIndex = monitor.getItem().index;
        const hoverIndex = props.index;

        if (dragIndex === hoverIndex) {
            return;
        }

        props.moveBlock(dragIndex, hoverIndex);
        monitor.getItem().index = hoverIndex;
    }
};

type DraggableBlockProps = {
    block: PromptTemplateBlock;
    index: number;
    canRemove: boolean;
    onBlockChange: (index: number, field: keyof PromptTemplateBlock, value: string) => void;
    onRemoveBlock: (index: number) => void;
    moveBlock: (dragIndex: number, hoverIndex: number) => void;
    activeKey?: string[];
    onCollapseChange: (key: string | string[]) => void;
    dragingIndex: number;
    onDragStart: (index: number) => void;
    templateStructure: PromptTemplateStructureEnum;
    isOver: boolean;
    connectDragSource: DragElementWrapper<DragSourceOptions>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    connectDropTarget: DragElementWrapper<any>;
};

const DraggableBlockComponent: React.FC<DraggableBlockProps> = ({
    block,
    index,
    canRemove,
    onBlockChange,
    onRemoveBlock,
    isOver,
    connectDragSource,
    connectDropTarget,
    activeKey,
    onCollapseChange,
    dragingIndex,
    templateStructure
}) => {
    const [commentVisible, setCommentVisible] = React.useState(false);
    const [tempComment, setTempComment] = React.useState(block.comment || '');

    React.useEffect(() => {
        if (!commentVisible) {
            setTempComment(block.comment || '');
        }
    }, [block.comment, commentVisible]);

    const blockKey = `block-${index}`;
    const isExpanded = activeKey?.includes(blockKey);

    let className = 'draggable-block';
    if (isOver) {
        if (index > dragingIndex) {
            className += ' drop-over-downward';
        }
        if (index < dragingIndex) {
            className += ' drop-over-upward';
        }
    }

    const handleHeaderClick = () => {
        const newActiveKey = isExpanded
            ? activeKey?.filter(key => key !== blockKey) || []
            : [...(activeKey || []), blockKey];
        onCollapseChange(newActiveKey);
    };

    const handleCommentSave = () => {
        onBlockChange(index, 'comment', tempComment);
        setCommentVisible(false);
    };

    const handleCommentCancel = () => {
        setTempComment(block.comment || '');
        setCommentVisible(false);
    };

    const handleCommentVisibleChange = (visible: boolean) => {
        if (visible) {
            setTempComment(block.comment || '');
        }
        setCommentVisible(visible);
    };

    const commentContent = (
        <div className="prompt-template-comment-popover-content">
            <div className="comment-form-field">
                <label className="comment-label">Internal note</label>
                <TextArea
                    value={tempComment}
                    onChange={e => setTempComment(e.target.value)}
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    placeholder="Enter internal note..."
                />
            </div>
            <div className="comment-actions">
                <Button size="small" onClick={handleCommentCancel}>
                    Cancel
                </Button>
                <Button size="small" type="primary" onClick={handleCommentSave}>
                    Save
                </Button>
            </div>
        </div>
    );

    // Show block type icon only when template is orchestrated and block type is not plain
    const shouldShowBlockTypeIcon =
        templateStructure === PromptTemplateStructureEnum.Orchestrated &&
        block.blockType !== PromptTemplateBlockTypeEnum.Plain;

    const panelHeader = (
        <div className="block-header" onClick={handleHeaderClick}>
            <div className="block-header-left">
                {connectDragSource(
                    <div className="drag-handle">
                        <DragOutlined />
                    </div>
                )}{' '}
                {/* Display block type icon for orchestrated templates with non-plain types */}
                {shouldShowBlockTypeIcon && (
                    <Tooltip title={`Block Type: ${getBlockTypeLabel(block.blockType)}`}>
                        <span className="block-type-icon">{getBlockTypeIcon(block.blockType)}</span>
                    </Tooltip>
                )}
                <div>{block.title && <span className="block-title"> {block.title}</span>}</div>
            </div>
            <div className="block-header-right">
                <div className="token-count">
                    {block.body
                        ? block.body
                              .trim()
                              .split(/\s+/)
                              .filter(word => word.length > 0).length
                        : 0}{' '}
                    tokens
                </div>
                <Tooltip title={block.comment && block.comment.trim() !== '' ? block.comment : ''}>
                    <FlagOutlined
                        className={`comment-flag ${
                            block.comment && block.comment.trim() !== '' ? 'has-comment' : 'no-comment'
                        }`}
                    />
                </Tooltip>
                {canRemove && (
                    <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={e => {
                            e.stopPropagation();
                            Modal.confirm({
                                title: 'Delete Block',
                                content: 'Are you sure you want to delete this block?',
                                okText: 'Yes',
                                cancelText: 'Cancel',
                                onOk: () => {
                                    onRemoveBlock(index);
                                }
                            });
                        }}
                        danger
                    />
                )}
            </div>
        </div>
    );

    return connectDropTarget(
        <div className={className}>
            <Collapse activeKey={activeKey} onChange={onCollapseChange} ghost>
                <Panel key={blockKey} header={panelHeader} showArrow={false}>
                    <div className="block-content">
                        <div className="form-field">
                            <label className="field-label">Title</label>
                            <div className="title-input-container">
                                <Input
                                    value={block.title}
                                    onChange={e => onBlockChange(index, 'title', e.target.value)}
                                    placeholder={`Title for block ${index + 1}`}
                                    style={{ flex: 1 }}
                                />
                                <Popover
                                    content={commentContent}
                                    title={null}
                                    trigger="click"
                                    visible={commentVisible}
                                    onVisibleChange={handleCommentVisibleChange}
                                    placement="bottomRight"
                                >
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<CommentOutlined />}
                                        style={{
                                            color: '#8c8c8c'
                                        }}
                                    />
                                </Popover>
                            </div>
                        </div>

                        {/* Block Type Selector - Only visible when template structure is Orchestrated */}
                        {templateStructure === PromptTemplateStructureEnum.Orchestrated && (
                            <div className="form-field">
                                <label className="field-label">Block Type</label>
                                <Select
                                    value={block.blockType}
                                    onChange={(value: PromptTemplateBlockTypeEnum) =>
                                        onBlockChange(index, 'blockType', value)
                                    }
                                    style={{ width: '100%' }}
                                    placeholder="Select block type"
                                >
                                    <Select.Option value={PromptTemplateBlockTypeEnum.Preprocessing}>
                                        Preprocessing
                                    </Select.Option>
                                    <Select.Option value={PromptTemplateBlockTypeEnum.Processing}>
                                        Processing
                                    </Select.Option>
                                    <Select.Option value={PromptTemplateBlockTypeEnum.Postprocessing}>
                                        Postprocessing
                                    </Select.Option>
                                </Select>
                            </div>
                        )}

                        <div className="form-field textarea-field">
                            <label className="field-label">Body</label>
                            <TextArea
                                value={block.body || ''}
                                onChange={e => onBlockChange(index, 'body', e.target.value)}
                                autoSize={{ minRows: 6 }}
                                placeholder={`Enter text for block ${index + 1}`}
                            />
                        </div>
                    </div>
                </Panel>
            </Collapse>
        </div>
    );
};

export const DraggableBlock = DropTarget('block', blockTarget, (connect, monitor) => ({
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver()
}))(
    DragSource('block', blockSource, connect => ({
        connectDragSource: connect.dragSource()
    }))(DraggableBlockComponent)
);
